import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipForward, SkipBack, Volume2, VolumeX, X } from "lucide-react";
import { jumpTo } from "./useMatterport";

export function TourBar({ sdk, stops, big, onPlay, transition = "fly", playSignal = 0, narrate = true, speed = "normal" }) {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [active, setActive] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!playing || !stops[idx]) return undefined;
    const stop = stops[idx];
    const next = () => {
      if (idx + 1 < stops.length) setIdx(idx + 1);
      else { setPlaying(false); }
    };
    jumpTo(sdk, stop.pose, transition, speed);
    const synth = window.speechSynthesis;
    const speech = stop.voice || (narrate ? stop.caption : "");
    if (speech && !muted && synth) {
      const u = new SpeechSynthesisUtterance(speech);
      u.rate = 0.98;
      u.onend = () => { timer.current = setTimeout(next, 1500); };
      synth.cancel();
      synth.speak(u);
    } else {
      timer.current = setTimeout(next, 7000);
    }
    return () => {
      clearTimeout(timer.current);
      synth?.cancel();
    };
  }, [playing, idx, muted, sdk, stops, transition, narrate, speed]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (playSignal && stops.length) { setIdx(0); setActive(true); setPlaying(true); onPlay?.(); } }, [playSignal]);

  if (!stops.length) return null;
  const btn = big ? "w-16 h-16" : "w-11 h-11";
  const start = () => { setActive(true); setPlaying(true); onPlay?.(); };
  if (!active) {
    return (
      <button onClick={start} className={`glass-dark rounded-full ${big ? "h-16 px-8 text-lg" : "h-12 px-6 text-sm"} font-semibold inline-flex items-center gap-2 shadow-2xl`} data-testid="tour-play-button">
        <Play className="w-5 h-5 fill-current" />Play guided tour
      </button>
    );
  }
  const stop = stops[idx];
  return (
    <div className="flex flex-col items-center gap-3 w-full max-w-xl" data-testid="tour-bar">
      {stop?.caption && <div className={`glass-dark rounded-2xl px-5 py-3 text-center ${big ? "text-xl" : "text-sm sm:text-base"} rise`} key={stop.id} data-testid="tour-caption">{stop.caption}</div>}
      <div className="glass-dark rounded-full p-1.5 flex items-center gap-1 shadow-2xl">
        <button className={`${btn} rounded-full grid place-items-center hover:bg-white/10`} onClick={() => setIdx(Math.max(0, idx - 1))} data-testid="tour-prev-button"><SkipBack className="w-5 h-5" /></button>
        <button className={`${btn} rounded-full grid place-items-center bg-white text-slate-900`} onClick={() => setPlaying(!playing)} data-testid="tour-toggle-button">
          {playing ? <Pause className="w-5 h-5 fill-current" /> : <Play className="w-5 h-5 fill-current" />}
        </button>
        <button className={`${btn} rounded-full grid place-items-center hover:bg-white/10`} onClick={() => setIdx(Math.min(stops.length - 1, idx + 1))} data-testid="tour-skip-button"><SkipForward className="w-5 h-5" /></button>
        <span className="px-3 text-xs text-white/70 tabular-nums" data-testid="tour-progress">{idx + 1} / {stops.length}</span>
        <button className={`${btn} rounded-full grid place-items-center hover:bg-white/10`} onClick={() => setMuted(!muted)} data-testid="tour-mute-button">{muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</button>
        <button className={`${btn} rounded-full grid place-items-center hover:bg-white/10`} onClick={() => { setPlaying(false); setActive(false); setIdx(0); }} data-testid="tour-close-button"><X className="w-5 h-5" /></button>
      </div>
    </div>
  );
}
